import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Shield, Edit2, UserCheck, Users, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${getBackendUrl()}/api`;

const MODULES = [
  { key: 'employees', label: 'Employees Management', icon: '👥' },
  { key: 'batches', label: 'Batches', icon: '🎓' },
  { key: 'subjects', label: 'Subjects', icon: '📚' },
  { key: 'classrooms', label: 'Classrooms', icon: '🏫' },
  { key: 'sessions', label: 'Sessions', icon: '📅' },
  { key: 'timetables', label: 'Timetables', icon: '🗓️' },
  { key: 'payroll', label: 'Payroll', icon: '💰' },
  { key: 'leaves', label: 'Leaves', icon: '🏖️' },
  { key: 'expenses', label: 'Expenses', icon: '💳' },
  { key: 'teachers', label: 'Teachers', icon: '👨‍🏫' },
  { key: 'time_slots', label: 'Time Slots', icon: '⏰' },
  { key: 'holidays', label: 'Holidays', icon: '🎉' }
];

const PERMISSIONS = [
  { key: 'view', label: 'View', color: 'blue' },
  { key: 'create', label: 'Create', color: 'green' },
  { key: 'edit', label: 'Edit', color: 'yellow' },
  { key: 'delete', label: 'Delete', color: 'red' },
  { key: 'export', label: 'Export', color: 'purple' },
  { key: 'import_data', label: 'Import', color: 'indigo' },
  { key: 'approve', label: 'Approve', color: 'teal' },
  { key: 'publish', label: 'Publish', color: 'cyan' },
  { key: 'download', label: 'Download', color: 'pink' }
];

const AdminPermissions = () => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [expandedModule, setExpandedModule] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('[AdminPermissions] Fetching admin users...');
      console.log('[AdminPermissions] API URL:', `${API}/admin/permissions`);
      console.log('[AdminPermissions] Token exists:', !!token);
      
      const response = await axios.get(`${API}/admin/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[AdminPermissions] Response status:', response.status);
      console.log('[AdminPermissions] Response data:', response.data);
      console.log('[AdminPermissions] Is array?:', Array.isArray(response.data));
      
      // Ensure response.data is an array
      const users = Array.isArray(response.data) ? response.data : [];
      console.log('[AdminPermissions] Setting admin users:', users.length);
      setAdminUsers(users);
      setLoading(false);
    } catch (error) {
      console.error('[AdminPermissions] Error fetching admin users:', error);
      console.error('[AdminPermissions] Error response:', error.response);
      console.error('[AdminPermissions] Error message:', error.message);
      toast.error(`Failed to load admin users: ${error.response?.data?.detail || error.message}`);
      setAdminUsers([]); // Set to empty array on error
      setLoading(false);
    }
  };

  const handleEditPermissions = async (admin) => {
    try {
      setEditingAdmin(admin);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/permissions/${admin.user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    }
  };

  const handlePermissionChange = (module, permission, value) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permission]: value
      }
    }));
  };

  const handleSelectAll = (module) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        view: true,
        create: true,
        edit: true,
        delete: true,
        export: true,
        import_data: true,
        approve: true,
        publish: true,
        download: true
      }
    }));
  };

  const handleDeselectAll = (module) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        view: false,
        create: false,
        edit: false,
        delete: false,
        export: false,
        import_data: false,
        approve: false,
        publish: false,
        download: false
      }
    }));
  };

  const handleSelectAllModules = () => {
    const allPermissions = {};
    MODULES.forEach(module => {
      allPermissions[module.key] = {
        view: true,
        create: true,
        edit: true,
        delete: true,
        export: true,
        import_data: true,
        approve: true,
        publish: true,
        download: true
      };
    });
    setPermissions(prev => ({ ...prev, ...allPermissions }));
    toast.success('All permissions granted for all modules');
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        admin_user_id: editingAdmin.user_id
      };
      
      // Add module permissions
      MODULES.forEach(module => {
        if (permissions[module.key]) {
          payload[module.key] = permissions[module.key];
        }
      });

      await axios.post(`${API}/admin/permissions`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Permissions saved successfully');
      setEditingAdmin(null);
      fetchAdminUsers();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8 text-blue-600" />
            Admin Permissions Management
          </h1>
          <p className="text-gray-600 mt-1">Manage permissions for admin users</p>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-gray-500">Loading admin users...</p>
          </CardContent>
        </Card>
      ) : adminUsers.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-gray-500">No admin users found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {adminUsers.map(admin => (
            <Card key={admin.user_id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{admin.name}</CardTitle>
                      <p className="text-sm text-gray-600">{admin.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={admin.role === 'super_admin' ? 'default' : 'secondary'}>
                      {admin.role === 'super_admin' ? '👑 Super Admin' : '🔑 Admin'}
                    </Badge>
                    {admin.has_permissions ? (
                      <Badge variant="default" className="bg-green-500">
                        <UserCheck className="w-3 h-3 mr-1" />
                        Configured
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        No Permissions
                      </Badge>
                    )}
                    {admin.role === 'admin' && (
                      <Button
                        onClick={() => handleEditPermissions(admin)}
                        size="sm"
                        variant="outline"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Permissions
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Permissions Modal */}
      <Dialog open={!!editingAdmin} onOpenChange={() => setEditingAdmin(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Permissions for {editingAdmin?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium">Quick Actions:</p>
              <Button onClick={handleSelectAllModules} size="sm" variant="default">
                Grant All Permissions
              </Button>
            </div>

            {MODULES.map(module => {
              const modulePerms = permissions[module.key] || {};
              const hasAnyPermission = Object.values(modulePerms).some(v => v);
              
              return (
                <Card key={module.key} className={hasAnyPermission ? 'border-green-300' : ''}>
                  <CardHeader className="cursor-pointer" onClick={() => setExpandedModule(expandedModule === module.key ? null : module.key)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{module.icon}</span>
                        <div>
                          <h3 className="font-semibold">{module.label}</h3>
                          <p className="text-xs text-gray-500">
                            {Object.entries(modulePerms).filter(([_, v]) => v).length} of {PERMISSIONS.length} permissions granted
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAll(module.key);
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          Select All
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeselectAll(module.key);
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          Clear
                        </Button>
                        {expandedModule === module.key ? <ChevronUp /> : <ChevronDown />}
                      </div>
                    </div>
                  </CardHeader>
                  {expandedModule === module.key && (
                    <CardContent>
                      <div className="grid grid-cols-3 gap-3">
                        {PERMISSIONS.map(perm => (
                          <div key={perm.key} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${module.key}-${perm.key}`}
                              checked={modulePerms[perm.key] || false}
                              onCheckedChange={(checked) => handlePermissionChange(module.key, perm.key, checked)}
                            />
                            <label
                              htmlFor={`${module.key}-${perm.key}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {perm.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            <div className="flex justify-end gap-3 pt-4">
              <Button onClick={() => setEditingAdmin(null)} variant="outline" disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSavePermissions} disabled={saving}>
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPermissions;
